import React from 'react';
import { toast } from '../ui/sonner';
import { Bot, ChevronDown, ChevronLeft, ChevronRight, Clock, FileText, Mail, MessageSquare, Phone, X } from 'lucide-react';

// Extracted from EmployerDashboard.js — receives everything it needs via `ctx`.
export default function EmployerHelpSupportModal({ ctx }) {
  const { helpView, setHelpView, setShowAiSupport, setShowHelpSupport, showHelpSupport } = ctx;
  return (
    <>
    {showHelpSupport && (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="help-support-modal">
        <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">
          <div className="bg-gradient-to-r from-violet-500 to-purple-600 px-6 py-5 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Help & Support</h2>
                <p className="text-sm text-white/70">How can we help?</p>
              </div>
            </div>
            <button onClick={() => setShowHelpSupport(false)} className="p-1.5 hover:bg-white/20 rounded-lg transition">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {helpView === 'main' && (
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <button onClick={() => { setShowHelpSupport(false); setShowAiSupport(true); }}
                className="w-full flex items-center gap-4 p-5 bg-blue-50 hover:bg-blue-100 rounded-xl border border-blue-100 transition text-left group" data-testid="help-ai-chat">
                <div className="p-3 bg-blue-500 rounded-xl flex-shrink-0"><Bot className="w-6 h-6 text-white" /></div>
                <div className="flex-1">
                  <p className="text-base font-bold text-slate-900">Chat with AI Assistant</p>
                  <p className="text-sm text-slate-500">Get instant answers to your questions</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600" />
              </button>
              <button onClick={() => setHelpView('faqs')}
                className="w-full flex items-center gap-4 p-5 bg-purple-50 hover:bg-purple-100 rounded-xl border border-purple-100 transition text-left group" data-testid="help-faqs">
                <div className="p-3 bg-purple-500 rounded-xl flex-shrink-0"><FileText className="w-6 h-6 text-white" /></div>
                <div className="flex-1">
                  <p className="text-base font-bold text-slate-900">Browse FAQs</p>
                  <p className="text-sm text-slate-500">Find answers to common questions</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600" />
              </button>
              <button onClick={() => setHelpView('contact')}
                className="w-full flex items-center gap-4 p-5 bg-emerald-50 hover:bg-emerald-100 rounded-xl border border-emerald-100 transition text-left group" data-testid="help-contact">
                <div className="p-3 bg-emerald-500 rounded-xl flex-shrink-0"><Phone className="w-6 h-6 text-white" /></div>
                <div className="flex-1">
                  <p className="text-base font-bold text-slate-900">Contact Support</p>
                  <p className="text-sm text-slate-500">Email, call, or request a callback</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600" />
              </button>
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <div className="flex items-center gap-2 mb-2"><Clock className="w-4 h-4 text-slate-500" /><p className="text-sm font-bold text-slate-800">Support Hours</p></div>
                <p className="text-sm text-slate-600">Monday - Friday: 8AM - 8PM EST</p>
                <p className="text-sm text-slate-600">Saturday - Sunday: 9AM - 5PM EST</p>
              </div>
            </div>
          )}

          {helpView === 'faqs' && (
            <div className="p-5 space-y-3 overflow-y-auto flex-1">
              <button onClick={() => setHelpView('main')} className="flex items-center gap-1 text-sm text-indigo-600 hover:underline mb-2"><ChevronLeft className="w-4 h-4" /> Back</button>
              <h3 className="text-lg font-bold text-slate-900">Frequently Asked Questions</h3>
              {[
                { q: 'How do I post a new job?', a: 'Go to "My Jobs" tab and click "Post Job with AI" or use the manual form. Fill in job details, location, pay rate, and required skills.' },
                { q: 'How does worker matching work?', a: 'Our AI matches workers based on skills, certifications, past performance, reliability, and availability. Go to Workers > Worker Matching.' },
                { q: 'How do I approve a worker for a shift?', a: 'When workers apply, you\'ll see applications in job details. Review their profile and click Approve or Reject.' },
                { q: 'How do I track worker attendance?', a: 'Use the "Live Workforce" dashboard under Workers to see real-time check-ins, no-shows, late arrivals, and overtime alerts.' },
                { q: 'How do I send messages to workers?', a: 'Use the Communication Hub under Workers. Broadcast to all workers or send direct messages to individuals.' },
                { q: 'How do I manage shift schedules?', a: 'Go to Workers > Schedule for a calendar view. Drag and drop shifts to reschedule.' },
                { q: 'How do I set up automation rules?', a: 'Go to Settings and scroll to "Shift Automation & Rules." Configure auto-approve, auto-reject, and reminder settings.' },
                { q: 'What is the geofence setting?', a: 'Geofence controls whether workers must be near the job site to check in. "Soft" shows a warning, "Hard" blocks check-in outside the radius.' },
              ].map((faq) => (
                <details key={faq.q} className="bg-white border border-slate-200 rounded-xl overflow-hidden group">
                  <summary className="px-4 py-3 cursor-pointer text-sm font-semibold text-slate-800 hover:bg-slate-50 list-none flex items-center justify-between">
                    {faq.q}<ChevronDown className="w-4 h-4 text-slate-400 group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="px-4 pb-3 text-sm text-slate-600 border-t border-slate-100 pt-2">{faq.a}</div>
                </details>
              ))}
            </div>
          )}

          {helpView === 'contact' && (
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <button onClick={() => setHelpView('main')} className="flex items-center gap-1 text-sm text-indigo-600 hover:underline mb-2"><ChevronLeft className="w-4 h-4" /> Back</button>
              <h3 className="text-lg font-bold text-slate-900">Contact Support</h3>
              <a href="mailto:support@scensobconnect.com" className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl border border-blue-100 hover:bg-blue-100 transition">
                <div className="p-2.5 bg-blue-500 rounded-lg"><Mail className="w-5 h-5 text-white" /></div>
                <div><p className="text-sm font-bold text-slate-900">Email Support</p><p className="text-xs text-slate-500">support@scensobconnect.com</p></div>
              </a>
              <a href="tel:+18005236762" className="flex items-center gap-4 p-4 bg-emerald-50 rounded-xl border border-emerald-100 hover:bg-emerald-100 transition">
                <div className="p-2.5 bg-emerald-500 rounded-lg"><Phone className="w-5 h-5 text-white" /></div>
                <div><p className="text-sm font-bold text-slate-900">Call Us</p><p className="text-xs text-slate-500">1-800-SCENSOB (1-800-523-6762)</p></div>
              </a>
              <button onClick={() => toast.success('Callback request submitted! We\'ll call you within 30 minutes.')}
                className="w-full flex items-center gap-4 p-4 bg-amber-50 rounded-xl border border-amber-100 hover:bg-amber-100 transition text-left" data-testid="request-callback">
                <div className="p-2.5 bg-amber-500 rounded-lg"><Phone className="w-5 h-5 text-white" /></div>
                <div><p className="text-sm font-bold text-slate-900">Request a Callback</p><p className="text-xs text-slate-500">We'll call you back within 30 minutes</p></div>
              </button>
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <div className="flex items-center gap-2 mb-2"><Clock className="w-4 h-4 text-slate-500" /><p className="text-sm font-bold text-slate-800">Support Hours</p></div>
                <p className="text-sm text-slate-600">Monday - Friday: 8AM - 8PM EST</p>
                <p className="text-sm text-slate-600">Saturday - Sunday: 9AM - 5PM EST</p>
              </div>
            </div>
          )}
        </div>
      </div>
    )}
    </>
  );
}
